import {
    useCallback,
} from 'react'

import useSelectorLongTermLease from 'app/Apps/OrderManagement/LongTermLease/hooks/useSelectorLongTermLease'
import StatusHandler from 'app/shared-components/StatusHandler'
import Select from 'app/shared-components/Select'
import {
    LongTermLeaseSelectorType,
} from 'app/Apps/OrderManagement/LongTermLease/longTermLease.types'
import useGetLongTermLease from 'app/Apps/OrderManagement/LongTermLease/hooks/useGetLongTermLease'
import SkyNetLink, {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes/skyNetRoutes.utils'
import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'

const convertToSelectorOptions = (longTermLeases: LongTermLeaseSelectorType[]) => {
    return longTermLeases?.reduce((acc, element) => {
        return {
            ...acc,
            [element.id]: element.longTermLeaseNumber,
        }
    }, {})
}

type Props = {
    value?: {
        id: number,
    },
    title?: string,
    disabled?: boolean,
    required?: boolean,
    onChange: (value: { id: number }) => void,
    pricingAccountId?: number,
}

const defaultProps: Partial<Props> = {
    value: undefined,
    title: 'Long-Term Lease',
    disabled: false,
    required: false,
    pricingAccountId: undefined,
}

const LongTermLeaseSelector = ({
    value,
    onChange,
    title,
    disabled,
    required,
    pricingAccountId,
}: Props) => {
    const {
        LongTermLease: LongTermLeaseRoutes,
    } = useOrderManagementRoutes()

    const {
        data: longTermLeases,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useSelectorLongTermLease({
        includeFilters: {
            pricingAccountId: pricingAccountId ? [pricingAccountId] : undefined,
        },
    }, !disabled)

    const {
        data: longTermLease,
    } = useGetLongTermLease(disabled ? value?.id : null)

    const handleChange = useCallback((val: number) => {
        onChange(val ? {
            id: val,
        } : undefined)
    }, [onChange])

    if (disabled) {
        const link = value?.id ? generateSkyNetLink({
            domainPath: LongTermLeaseRoutes.Overview.path,
            params: {
                key: value?.id,
            },
        }) : null

        return (
            <SkyNetLink
                type={TypeLink.EXTERNAL}
                size={SizeLink.S}
                title={title}
                link={link}
                text={longTermLease?.longTermLeaseNumber}
            />
        )
    }

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <Select
                data-testid="long-term-lease-select"
                name="longTermLease"
                allowedValues={convertToSelectorOptions(longTermLeases)}
                title={title}
                disabled={disabled}
                required={required}
                value={value?.id}
                onChange={handleChange}
            />
        </StatusHandler>
    )
}

LongTermLeaseSelector.defaultProps = defaultProps

export default LongTermLeaseSelector
